import type { FC, ReactElement } from 'react'

import { Button } from '@skytech/ui/Button'
import { getEntries } from '@skytech/utils'
import { useMutation } from '@tanstack/react-query'
import { Formik, Form as FormikForm } from 'formik'

import Text from '@/app/translations/Text'
import type { OffboardingForm, TerminationType as TerminationTypeType } from '@/app/types'
import { useSubmitFormMessage, useOffBoardingForm } from '@/forms/hooks'

import UserSubmissionDate from '../UserSubmissionDate'

import AssetHandling from './AssetHandling'
import Identification from './Identification'
import offBoardingResources from './resources'
import { TerminationType } from './TerminationType'
import type { BuyoutType, OffBoardingPayload } from './types'

type FormFields = {
  birthDateOrgNo: string
  isCreditCheckAccepted: boolean
  terminationType: TerminationTypeType | string
  [key: number]: BuyoutType | string
}

const getInitValues = (data: OffboardingForm) => {
  const assetsInitValues = data.assets.reduce((acc: Record<number, BuyoutType | string>, asset) => {
    acc[asset.id] = !asset.isBuyoutAllowed
      ? ('RETURN' as const)
      : asset.isBuyout
        ? ('BUYOUT' as const)
        : asset.isReturn
          ? ('RETURN' as const)
          : ''

    return acc
  }, {})

  const initValues = {
    terminationType:
      data.type === ('USER_DECIDES' as const)
        ? ''
        : data.type === 'TRANSFER'
          ? ('TRANSFER' as const)
          : ('CANCELLATION' as const),
    birthDateOrgNo: data.birthDateOrgNo || '',
    isCreditCheckAccepted: Boolean(data.acceptanceGiven),
    ...assetsInitValues,
  }

  return initValues
}

const getSubmitValues = (data: FormFields, coType: 'M' | 'E') => {
  const { terminationType, birthDateOrgNo, isCreditCheckAccepted, ...rest } = data
  const assets = getEntries(rest).map(([assetId, selectedBuyoutType]) => ({
    assetId: Number(assetId),
    isBuyout: selectedBuyoutType === 'BUYOUT',
    isReturn: selectedBuyoutType === 'RETURN',
  }))

  return {
    type: (coType === 'E' ? 'USER_DECIDES' : terminationType) as TerminationTypeType,
    birth_date_org_no: birthDateOrgNo || null,
    assets,
    acceptance_given: isCreditCheckAccepted,
  }
}

const Form: FC = () => {
  const { SubmitFormMessagePopup, onSubmitFormSuccess, onSubmitFormError } = useSubmitFormMessage()

  const { data, refetch } = useOffBoardingForm()
  if (!data) return null

  const offBoardingMutation = useMutation(
    (formData: OffBoardingPayload) => offBoardingResources.post(formData),
    {
      onSuccess() {
        onSubmitFormSuccess()
        refetch()
      },
      onError: onSubmitFormError,
    }
  )

  return (
    <div>
      <Formik
        validateOnBlur={false}
        initialValues={getInitValues(data)}
        validate={values => {
          const errors: Partial<Record<keyof FormFields, string | ReactElement>> = {}
          const { terminationType, birthDateOrgNo, isCreditCheckAccepted, ...rest } = values

          if (!terminationType && data.coType !== 'E') {
            errors.terminationType = <Text id="This field is mandatory" />
          }

          if (!birthDateOrgNo && terminationType === 'TRANSFER') {
            errors.birthDateOrgNo = <Text id="This field is mandatory" />
          }

          if (!isCreditCheckAccepted && terminationType === 'TRANSFER') {
            errors.isCreditCheckAccepted = <Text id="This field is mandatory" />
          }

          getEntries(rest).forEach(([assetId, buyoutType]) => {
            if (buyoutType) return
            errors[assetId] = <Text id="This field is mandatory" />
          })

          return errors
        }}
        onSubmit={formData => offBoardingMutation.mutate(getSubmitValues(formData, data.coType))}
      >
        <FormikForm>
          {data.coType !== 'E' && <TerminationType />}

          <Identification isEditable={data?.isEditable} />

          <AssetHandling currency={data?.currency} />

          <section className="submit-section">
            <Button
              type="submit"
              disabled={!data?.isEditable}
              isLoading={offBoardingMutation.isLoading}
            >
              <Text id="Send inn skjema" />
            </Button>
          </section>
        </FormikForm>
      </Formik>

      <SubmitFormMessagePopup />
    </div>
  )
}

const OffBoarding: FC = () => (
  <div className="offboarding">
    <UserSubmissionDate />
    <Form />
  </div>
)

export default OffBoarding
